import { css } from "@emotion/react";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "@moneyforward/mfx_biz_react_components";
import { color } from "@/styles/theme";
import { PasswordFormField, UsernameFormField } from "@/components/molecules";
import { useIsSpMode } from "@/hooks";
import {
  loginIDValidation,
  requiredValidation,
} from "@/components/validations";

type FormValues = {
  username: string;
  password: string;
};

type Props = {
  onSubmit?: ({ username, password }: FormValues) => void;
  // When provided, renders 2-step form (Aegis path). When absent, renders single-step form.
  onNext?: (username: string) => Promise<boolean>; // true = redirecting (Aegis), false = show password (Cognito)
  isResolvingIdp?: boolean;
  step: "username" | "password";
  onStepChange: (step: "username" | "password") => void;
};

export const SignInForm = (props: Props) => {
  const isSpMode = useIsSpMode();
  const isTwoStep = !!props.onNext;

  const { step, onStepChange: setStep } = props;

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const handleFormSubmit = methods.handleSubmit(async (values) => {
    if (isTwoStep && step === "username") {
      const redirecting = await props.onNext(values.username);
      if (!redirecting) {
        setStep("password");
      }
    } else {
      props.onSubmit?.(values);
    }
  });

  const isSubmitDisabled =
    isTwoStep && step === "username"
      ? props.isResolvingIdp
      : methods.formState.isSubmitting;

  return (
    <div css={container}>
      <div css={!isSpMode ? box : spModeBox}>
        <FormProvider {...methods}>
          <form onSubmit={handleFormSubmit} css={form}>
            <div css={!isSpMode ? inputList : spModeInputList}>
              <div css={listItem}>
                <UsernameFormField
                  name="username"
                  validate={loginIDValidation}
                  whetherToDisplayMailAddressMessage={true}
                  readOnly={isTwoStep && step === "password"}
                />
              </div>
              {(!isTwoStep || step === "password") && (
                <div css={listItem}>
                  <PasswordFormField
                    name="password"
                    usePasswordMeter={false}
                    registerOptions={{
                      validate: requiredValidation,
                    }}
                    enableAutofill={true}
                  />
                </div>
              )}
            </div>
            <div css={buttonWrapper}>
              <Button type="submit" disabled={isSubmitDisabled}>
                {isTwoStep && step === "username" ? "次へ" : "ログイン"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

const container = css({
  display: "flex",
  justifyContent: "center",
});

const box = css({
  width: "640px",
  minHeight: "210px",
  padding: "18px 32px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const spModeBox = css({
  width: "640px",
  minHeight: "210px",
  padding: "15px 17px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const form = css({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "32px",
});

const inputList = css({
  flex: 1,
});

const spModeInputList = css({
  flex: 1,
});

const listItem = css({
  marginBottom: "25px",
});

const buttonWrapper = css({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "8px",
});

const bizsolWrapper = css({
  padding: "25px 35px 25px 35px",
  backgroundColor: `${color.gray50}`,
});

const link = css({
  color: `${color.link}`,
});
